import React, { useState } from 'react';
import { useStore } from 'react-redux';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import { Box, Button, Chip, IconButton, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';

import { ChatEntry, useAssistantState } from '../../assistant/assistantStore';
import {
  addRecordingNote,
  cancelShowMeHow,
  finishShowMeHow,
  rateReply,
  sendFeedbackComment,
  startShowMeHow,
} from '../../assistant/feedback';
import { MAX_RECORDED_STEPS, RecordedStep } from '../../assistant/recorder';
import type { RootState } from '../../store';

function StepList({ steps }: { steps: readonly RecordedStep[] }) {
  return (
    <Box component="ol" sx={{ m: 0, pl: 2.5, maxHeight: 160, overflowY: 'auto' }}>
      {steps.map((step, index) => (
        <Typography key={index} component="li" variant="caption" sx={{ display: 'list-item', wordBreak: 'break-word' }}>
          {step.text}
        </Typography>
      ))}
    </Box>
  );
}

/**
 * Under each reply: a rating, sent with one click, and on request a box for what the designer would have wanted, with
 * Show me how to record them doing it. It all goes to the team with the conversation.
 */
export function FeedbackEntry({
  chatId,
  index,
  entry,
}: {
  chatId: string;
  index: number;
  entry: Extract<ChatEntry, { type: 'feedback' }>;
}) {
  const store = useStore<RootState>();
  const recording = useAssistantState((state) => state.recording);
  const [expanded, setExpanded] = useState(false);
  const [comment, setComment] = useState(entry.comment);
  const [showSteps, setShowSteps] = useState(false);
  const recordingHere = recording?.chatId === chatId && recording.entryIndex === index;
  const open = expanded || entry.recording != null || recordingHere;

  const rate = (rating: 'good' | 'bad') => {
    rateReply(chatId, index, rating);
    // Asking what went wrong is most useful right after a thumbs down.
    if (rating === 'bad' && entry.rating !== 'bad') setExpanded(true);
  };

  return (
    <Stack spacing={0.5} sx={{ alignSelf: 'stretch' }}>
      <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center' }}>
        <Tooltip title="Good reply">
          <IconButton size="small" aria-label="Good reply" onClick={() => rate('good')}>
            {entry.rating === 'good' ? <ThumbUpIcon fontSize="inherit" /> : <ThumbUpOutlinedIcon fontSize="inherit" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Bad reply">
          <IconButton size="small" aria-label="Bad reply" onClick={() => rate('bad')}>
            {entry.rating === 'bad' ? (
              <ThumbDownIcon fontSize="inherit" />
            ) : (
              <ThumbDownOutlinedIcon fontSize="inherit" />
            )}
          </IconButton>
        </Tooltip>
        {!open && (
          <Button size="small" onClick={() => setExpanded(true)} sx={{ textTransform: 'none', minWidth: 0 }}>
            Tell us more
          </Button>
        )}
        <Typography variant="caption" color={entry.status === 'failed' ? 'error' : 'text.secondary'} sx={{ ml: 1 }}>
          {entry.status === 'sending'
            ? 'Sending…'
            : entry.status === 'failed'
              ? 'Couldn’t send. Try again.'
              : entry.status === 'sent'
                ? 'Sent to the team. Thanks.'
                : ''}
        </Typography>
      </Stack>

      {open && (
        <Paper variant="outlined" sx={{ p: 1 }}>
          <Stack spacing={1}>
            <TextField
              size="small"
              multiline
              minRows={2}
              maxRows={8}
              label="What would you have wanted it to do?"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
            />
            {entry.recording != null && (
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Chip
                    size="small"
                    label={`Recording: ${entry.recording.steps.length} step${entry.recording.steps.length === 1 ? '' : 's'}${
                      entry.recording.notes.length === 0
                        ? ''
                        : `, ${entry.recording.notes.length} note${entry.recording.notes.length === 1 ? '' : 's'}`
                    }`}
                    onClick={() => setShowSteps(!showSteps)}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {showSteps ? 'Click to hide' : 'Click to review what was recorded'}
                  </Typography>
                </Stack>
                {showSteps && (
                  <StepList
                    steps={[
                      ...entry.recording.steps,
                      ...entry.recording.notes.map((note) => ({ ...note, text: `Note: ${note.text}` })),
                    ].sort((a, b) => a.at - b.at)}
                  />
                )}
              </Stack>
            )}
            <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
              <Tooltip title="Do it yourself in the app while the steps are recorded for the team">
                <span>
                  <Button
                    size="small"
                    startIcon={<FiberManualRecordIcon color="error" />}
                    disabled={recording != null}
                    onClick={() => startShowMeHow(chatId, index, store)}
                  >
                    {entry.recording == null ? 'Show me how' : 'Record again'}
                  </Button>
                </span>
              </Tooltip>
              <Button
                size="small"
                variant="contained"
                disabled={
                  recordingHere || entry.status === 'sending' || (comment.trim() === '' && entry.recording == null)
                }
                onClick={() => sendFeedbackComment(chatId, index, comment)}
              >
                Send
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}

/**
 * Shown in the window that's recording: what's been recorded so far, a box for notes on why, and Stop.
 */
export function RecordingBar({ chatId }: { chatId: string }) {
  const recording = useAssistantState((state) => state.recording);
  const [note, setNote] = useState('');
  if (recording?.chatId !== chatId) return null;

  const full = recording.steps.length >= MAX_RECORDED_STEPS;
  return (
    <Paper variant="outlined" sx={{ p: 1, borderColor: 'error.main' }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <FiberManualRecordIcon color="error" fontSize="small" />
          <Typography variant="body2" sx={{ flex: 1 }}>
            {full
              ? 'Recording is full. Stop to keep it.'
              : `Recording. Do it in the app; ${recording.steps.length} step${recording.steps.length === 1 ? '' : 's'} so far.`}
          </Typography>
          <Button size="small" onClick={cancelShowMeHow}>
            Cancel
          </Button>
          <Button size="small" variant="contained" color="error" onClick={finishShowMeHow}>
            Stop
          </Button>
        </Stack>
        {recording.steps.length > 0 && <StepList steps={recording.steps.slice(-3)} />}
        <TextField
          size="small"
          label="Add a note on why (Enter)"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addRecordingNote(note);
              setNote('');
            }
          }}
        />
      </Stack>
    </Paper>
  );
}
